/**
 * Module-level event bus for Business Inbox SSE events.
 *
 * The SSE connection to /api/inbox/events is opened once, independent of
 * whether InboxPage is mounted, so real-time updates (nav badges, toasts,
 * etc.) keep flowing app-wide. AppShell owns the lifecycle via
 * startInboxEventStream(); consumers subscribe via subscribeInboxEvents().
 *
 * Uses fetch-based SSE (not EventSource) so we can pass the JWT
 * Authorization header. Reconnects automatically with exponential
 * backoff on connection loss.
 */

import { useAuthStore } from '@/stores/authStore'
import { apiClient } from '@/services/apiClient'

export interface InboxEvent {
  type: string
  conversationId?: string
  messageId?: string
  callId?: string
  orderId?: string
  actor?: string
  /** SA-1029: `record_update_received` / `record_update_settled`. */
  orderNumber?: string
  updateId?: string
  updateStatus?: string
  /** SA-1047: what the record update is; an older server sends none, read as 'update'. */
  kind?: 'update' | 'response'
  interest?: { ownerId: string | null; assignedTo: string | null; seatRoles: string[] }
  /** SA-1143: the payload as sent, so a module reads its own event's fields without the core naming them. */
  raw?: Record<string, unknown>
}

const listeners = new Set<(e: InboxEvent) => void>()
let activeController: AbortController | null = null
let activeStarts = 0

function dispatch(e: InboxEvent) {
  for (const fn of [...listeners]) {
    try {
      fn(e)
    } catch {
      /* one bad listener must not break the bus */
    }
  }
}

export const __dispatchForTest = dispatch

/** Register a listener; returns unsubscribe. Safe before the stream starts. */
export function subscribeInboxEvents(fn: (e: InboxEvent) => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/** One SSE `data:` payload as the bus hands it on: snake_case read into camelCase, the payload itself kept as `raw`
 * (SA-1143). Null for one with no type (a heartbeat is `{}`); text that is not JSON throws, and the stream ignores it. */
export function toInboxEvent(text: string): InboxEvent | null {
  const raw = JSON.parse(text)
  if (!raw || typeof raw !== 'object' || !raw.type) return null
  return {
    type: raw.type,
    conversationId: raw.conversation_id,
    messageId: raw.message_id,
    callId: raw.call_id,
    orderId: raw.order_id,
    actor: raw.actor,
    orderNumber: raw.order_number,
    updateId: raw.update_id,
    updateStatus: raw.update_status,
    kind: raw.type === 'record_update_received' || raw.type === 'record_update_settled'
      ? (raw.kind === 'response' ? 'response' : 'update') : undefined,
    interest: raw.interest ? {
      ownerId: raw.interest.owner_id ?? null,
      assignedTo: raw.interest.assigned_to ?? null,
      seatRoles: Array.isArray(raw.interest.seat_roles) ? raw.interest.seat_roles : [],
    } : undefined,
    raw,
  }
}

async function connectSSE(controller: AbortController) {
  let retryDelay = 2000 // Start at 2s, increase on consecutive failures

  async function attempt() {
    if (controller.signal.aborted) return

    // Get fresh token on each connection attempt (handles token refresh)
    const token = useAuthStore.getState().accessToken
    if (!token) {
      // Not authenticated — retry after delay (login may be in progress)
      if (!controller.signal.aborted) {
        setTimeout(attempt, 3000)
      }
      return
    }

    try {
      // Use apiClient.fetch for automatic 401 handling + token refresh
      const response = await apiClient.fetch('/api/inbox/events', {
        signal: controller.signal,
      })

      if (!response.ok || !response.body) {
        // After apiClient retry logic, still failed — back off if still authed
        if (!controller.signal.aborted && useAuthStore.getState().accessToken) {
          setTimeout(attempt, retryDelay)
          retryDelay = Math.min(retryDelay * 1.5, 30000)
        }
        return
      }

      // Reset retry delay on successful connection
      retryDelay = 2000

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          const match = line.match(/^data:\s*(.+)$/m)
          if (match) {
            try {
              const event = toInboxEvent(match[1])
              if (event) dispatch(event)
            } catch {
              // Ignore parse errors (heartbeats are empty objects)
            }
          }
        }
      }

      // Stream ended normally — reconnect
      if (!controller.signal.aborted) {
        setTimeout(attempt, retryDelay)
      }
    } catch {
      // Connection closed or aborted — reconnect with backoff
      if (!controller.signal.aborted && useAuthStore.getState().accessToken) {
        setTimeout(attempt, retryDelay)
        retryDelay = Math.min(retryDelay * 1.5, 30000) // Cap at 30s
      }
    }
  }

  attempt()
}

/**
 * Open the SSE connection (idempotent — a second call while one is active
 * reuses it rather than opening a duplicate). Refcounted: every caller gets
 * a live handle back. The underlying connection starts on the first call
 * and is torn down only once every outstanding handle has called stop().
 * Each returned stop() is itself once-only, so calling it twice can't
 * double-decrement the count. Mounted by AppShell; any other caller (e.g. a
 * feature that needs the bus running outside AppShell's lifetime) can start
 * and stop its own handle safely.
 */
export function startInboxEventStream(): () => void {
  activeStarts += 1
  if (!activeController) {
    const controller = new AbortController()
    activeController = controller
    connectSSE(controller) // the moved loop; calls dispatch(...) per event
  }

  let stopped = false
  return () => {
    if (stopped) return
    stopped = true
    activeStarts -= 1
    if (activeStarts <= 0) {
      activeStarts = 0
      activeController?.abort()
      activeController = null
    }
  }
}

/**
 * SA-922: a stream connected as one identity must never serve the next. Abort
 * the live connection and open a new one; `connectSSE` reads the token of
 * whoever is signed in NOW. The refcount is untouched, and with no handle
 * held there is nothing to restart.
 */
export function restartInboxEventStream(): void {
  if (!activeController) return
  activeController.abort()
  const controller = new AbortController()
  activeController = controller
  connectSSE(controller)
}

/** Test-only introspection of the module-level stream state. */
export function __streamStateForTest(): { active: boolean; starts: number } {
  return { active: activeController !== null, starts: activeStarts }
}
